import React, {useMemo, useState} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {InitializeDatasetStepDataName} from 'analysis-tool/AnalysisStepDataNames';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepConfigurationViewTemplate, {
  StepConfigurationViewTemplateProps,
} from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {assert} from 'Asserts';
import {DataFrame, DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {InstrumentData} from 'instruments/instrument-data/InstrumentDataConstants';
import {getPreviewOutputStepData, PreviewOutput} from 'pipelines/network/PipelineDAO';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {
  AsyncState,
  AsyncStatus,
  getMostRecentData,
  NOT_FETCHED_ASYNC_STATE,
} from 'ui-library/hooks/useAsyncState';

import {ConnectionListingGraphQL} from '../../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import ChooseDifferentDataFrameCreationMethodButton from '../ChooseDifferentDataFrameCreationMethodButton';
import {
  getTemplateDataFrameColumns,
  selectedColumnsToColumnMappings,
  unselectColumnsIfColumnIsNotInTemplateColumns,
} from '../column-mapping/ColumnMappingUtil';
import {DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT} from '../DataFrameCreationConstants';
import {buildPreviewFailureMessage} from '../DataFrameCreationMessages';
import {DataFrameNameState} from '../DataFrameCreationTypes';
import {getSharedDataFrameCreationMethodProps, openSelectedItemForNewStep} from '../DataFrameCreationUtils';
import {useAutoUpdatePreview} from '../hooks/useAutoUpdatePreview';
import {DataFrameNameFieldProps} from '../steps/DataFrameNameField';
import ConnectionDataStepperForm from './ConnectionDataStepperForm';

export interface CreateDataFrameFromConnectionDataRequestData {
  dataFrameName: string;
}

const DEFAULT_DATA_FRAME_NAME_STATE: DataFrameNameState = {name: '', hasUserEdits: false};

const connectionIdGlobalState = makeUseGlobalClientState<string | null>(null);
const integrationAdapterIdGlobalState = makeUseGlobalClientState<string | null>(null);
const instrumentDataGlobalState = makeUseGlobalClientState<InstrumentData | null>(null);
const transformDataTypeGlobalState = makeUseGlobalClientState<string | null>(null);
const dataFrameNameGlobalState = makeUseGlobalClientState<DataFrameNameState>(DEFAULT_DATA_FRAME_NAME_STATE);

const isFinalizingGlobalState = makeUseGlobalClientState<boolean>(false);
const previewAsyncGlobalState =
  makeUseGlobalClientState<AsyncState<PreviewOutput<PipelineStepType.CONNECTION_DATA_DATASET>>>(
    NOT_FETCHED_ASYNC_STATE
  );

export default function CreateDataFrameFromConnectionDataView(
  props: MainViewComponentProps<AnalysisMainViewItemType.CONNECTION_DATA_DATASET_CONFIGURATION>
): JSX.Element {
  const [connectionId, setConnectionId] = connectionIdGlobalState.useGlobalClientState(props.clientStateId);
  const [integrationAdapterId, setIntegrationAdapterId] =
    integrationAdapterIdGlobalState.useGlobalClientState(props.clientStateId);
  const [instrumentData, setInstrumentData] = instrumentDataGlobalState.useGlobalClientState(
    props.clientStateId
  );
  const [transformDataType, setTransformDataType] = transformDataTypeGlobalState.useGlobalClientState(
    props.clientStateId
  );
  const [dataFrameNameState, setDataFrameNameState] = dataFrameNameGlobalState.useGlobalClientState(
    props.clientStateId
  );
  const [isFinalizing, setIsFinalizing] = isFinalizingGlobalState.useGlobalClientState(props.clientStateId);
  const templateDataFrameColumns = useMemo(
    () => getTemplateDataFrameColumns(props.analysis, props.selectedItem.stepIdToReplace),
    [props.analysis, props.selectedItem.stepIdToReplace]
  );
  const [selectedColumns, setSelectedColumns] = useState<Array<DataFrameColumnDef | null>>([]);
  const [isManuallyChanged, setIsManuallyChanged] = useState<boolean>(false);

  const [previewState, setPreviewState] = previewAsyncGlobalState.useGlobalClientState(props.clientStateId);
  useAutoUpdatePreview(
    instrumentData != null && transformDataType != null
      ? {
          stepType: PipelineStepType.CONNECTION_DATA_DATASET,
          stepConfig: {
            dataFrameName: '',
            connectionId: instrumentData.connectionId,
            connectionFileId: instrumentData.id,
            transformDataType,
            dataLocale: null,
          },
        }
      : null,
    (data, setAsyncState) => {
      AnalysisActions.runPreviewAnalysisStep<PipelineStepType.CONNECTION_DATA_DATASET>(
        data,
        {status: AsyncStatus.NOT_FETCHED},
        setAsyncState
      );
    },
    setPreviewState
  );

  const mostRecentData = getMostRecentData(previewState);
  const previewDataFrame: DataFrame | null =
    (mostRecentData &&
      getPreviewOutputStepData(
        mostRecentData,
        InitializeDatasetStepDataName.DATASET,
        PipelineStepDataKind.DATASET
      )[0]) ??
    null;

  const clearState = (): void => {
    setConnectionId(null);
    setIntegrationAdapterId(null);
    setInstrumentData(null);
    setTransformDataType(null);
    setDataFrameNameState(DEFAULT_DATA_FRAME_NAME_STATE);
  };

  async function onFinalize(): Promise<void> {
    assert(
      instrumentData != null && dataFrameNameState.name !== '' && transformDataType != null,
      'Tried to finalize with incomplete form'
    );

    setIsFinalizing(true);

    try {
      const newStep = await AnalysisActions.addOrReplaceAndRunAnalysisStep(
        props.analysis.id,
        {
          stepType: PipelineStepType.CONNECTION_DATA_DATASET,
          stepVersion: 0,
          stepConfig: {
            dataFrameName: dataFrameNameState.name,
            connectionId: instrumentData.connectionId,
            connectionFileId: instrumentData.id,
            transformDataType,
            dataLocale: null,
          },
        },
        props.selectedItem.stepIdToReplace
          ? {
              originalStepId: props.selectedItem.stepIdToReplace,
              columnMapping: selectedColumnsToColumnMappings(selectedColumns, templateDataFrameColumns),
              allDisplayNames: previewDataFrame!.columns.map((c) => c.displayName),
            }
          : undefined
      );
      openSelectedItemForNewStep(newStep, props.onCloseStepInitializationView);
      clearState();
    } finally {
      setIsFinalizing(false);
    }
  }

  const onCancel: StepConfigurationViewTemplateProps['onCancel'] = (...args) => {
    props.onCloseStepInitializationView(...args);
    clearState();
  };

  if (previewDataFrame?.columns) {
    unselectColumnsIfColumnIsNotInTemplateColumns(
      selectedColumns,
      previewDataFrame.columns,
      setSelectedColumns
    );
  }

  const dataFrameNameFieldProps: DataFrameNameFieldProps = {
    ...dataFrameNameState,
    onNameChange: (newName: string) => {
      setDataFrameNameState({name: newName, hasUserEdits: true});
    },
    disabled: false,
  };
  return (
    <StepConfigurationViewTemplate
      {...getSharedDataFrameCreationMethodProps(props.selectedItem)}
      title="Connection data"
      clientStateId={props.clientStateId}
      dataFrames={previewDataFrame ? [{...previewDataFrame, name: dataFrameNameState.name}] : []}
      disableFinalizeButton={
        dataFrameNameState.name.trim() === '' ||
        instrumentData == null ||
        transformDataType == null ||
        (props.selectedItem.stepIdToReplace != null && previewDataFrame == null)
      }
      isFinalizing={isFinalizing}
      isLoadingPreview={previewState.status === AsyncStatus.LOADING}
      messages={previewState.status === AsyncStatus.ERROR ? [buildPreviewFailureMessage()] : []}
      noPreviewPlaceholderOptions={{
        title: DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT,
        subtitle: 'Select a connection and file to see a preview of your table.',
      }}
      onCancel={onCancel}
      onFinalize={onFinalize}
      toolbar={
        <ChooseDifferentDataFrameCreationMethodButton
          onOpenStepInitializationView={(newSelectedItem: {
            type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD;
          }) => {
            props.onOpenStepInitializationView({
              ...newSelectedItem,
              stepIdToReplace: props.selectedItem.stepIdToReplace,
            });
          }}
        />
      }
    >
      <ConnectionDataStepperForm
        dataFrameNameFieldProps={dataFrameNameFieldProps}
        connectionId={connectionId}
        integrationAdapterId={integrationAdapterId}
        onChangeConnection={(connection: ConnectionListingGraphQL | null) => {
          if (connection) {
            setConnectionId(connection.id);
            setIntegrationAdapterId(connection.schema.integrationAdapterId);
            setTransformDataType(null);
            if (!dataFrameNameState.hasUserEdits) {
              setDataFrameNameState({name: `${connection.name} data`, hasUserEdits: false});
            }
          } else {
            setConnectionId(null);
            setIntegrationAdapterId(null);
            setInstrumentData(null);
            setTransformDataType(null);
          }
        }}
        transformDataType={transformDataType}
        onChangeTransformDataType={setTransformDataType}
        columnMappingProps={
          props.selectedItem.stepIdToReplace != null
            ? {
                templateDataFrameColumns,
                newDataFrameColumns: previewDataFrame?.columns ?? null,
                newDataFrameState: previewState.status,
                selectedColumns,
                setSelectedColumns,
                ignoreColumnValidationErrors: true,
                isManuallyChanged,
                setIsManuallyChanged,
              }
            : undefined
        }
        instrumentData={instrumentData}
        onChangeInstrumentData={setInstrumentData}
      />
    </StepConfigurationViewTemplate>
  );
}
